import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import StatusBadge from "./StatusBadge";

const ACTION_CONFIG = {
  approve: {
    title: "Aprobar y Activar Organización",
    icon: "UserCheck",
    iconColor: "var(--color-success)",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    confirmVariant: "success",
    confirmLabel: "Aprobar y Activar",
    description: "Al aprobar esta organización, se activará su cuenta y se enviará un correo de bienvenida al contacto registrado.",
  },
  reject: {
    title: "Rechazar Solicitud",
    icon: "UserX",
    iconColor: "var(--color-error)",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    confirmVariant: "destructive",
    confirmLabel: "Rechazar Solicitud",
    description: "Esta acción rechazará la solicitud de registro. La organización no podrá acceder al sistema.",
  },
  suspend: {
    title: "Suspender Usuario",
    icon: "PauseCircle",
    iconColor: "var(--color-warning)",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    confirmVariant: "warning",
    confirmLabel: "Suspender",
    description: "El usuario perderá acceso al sistema temporalmente. Podrá ser reactivado posteriormente.",
  },
  reactivate: {
    title: "Reactivar Usuario",
    icon: "PlayCircle",
    iconColor: "var(--color-primary)",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    confirmVariant: "default",
    confirmLabel: "Reactivar",
    description: "El usuario recuperará acceso completo al sistema según sus permisos asignados.",
  },
};

const ConfirmationModal = ({ isOpen, action, organization, onConfirm, onCancel, loading }) => {
  if (!isOpen || !organization) return null;

  const config = ACTION_CONFIG?.[action] || ACTION_CONFIG?.approve;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div
        className="w-full max-w-lg rounded-2xl shadow-xl overflow-hidden"
        style={{ backgroundColor: "var(--color-card)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className={`px-6 py-4 flex items-center gap-3 ${config?.bgColor} border-b ${config?.borderColor}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config?.bgColor} border ${config?.borderColor}`}>
            <Icon name={config?.icon} size={20} color={config?.iconColor} />
          </div>
          <h2 id="modal-title" className="font-heading font-bold text-base" style={{ color: "var(--color-foreground)" }}>
            {config?.title}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-sm font-caption mb-4" style={{ color: "var(--color-muted-foreground)" }}>
            {config?.description}
          </p>

          <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-background)" }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Razón Social</span>
              <span className="text-sm font-caption font-semibold" style={{ color: "var(--color-foreground)" }}>{organization?.businessName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>NIT</span>
              <span className="text-sm font-data" style={{ color: "var(--color-foreground)" }}>{organization?.nit}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Correo</span>
              <span className="text-sm font-caption" style={{ color: "var(--color-foreground)" }}>{organization?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Estado actual</span>
              <StatusBadge status={organization?.status} />
            </div>
          </div>

          {action === "approve" && (
            <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-teal-50 border border-teal-200">
              <Icon name="Mail" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs font-caption" style={{ color: "var(--color-primary)" }}>
                Se enviará automáticamente un correo de bienvenida a <strong>{organization?.email}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-3 border-t" style={{ borderColor: "var(--color-border)" }}>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button variant={config?.confirmVariant} onClick={onConfirm} loading={loading} iconName={config?.icon} iconPosition="left">
            {config?.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;